import { WHATSAPP_TEMPLATE_CATEGORY_LABELS, WHATSAPP_TEMPLATE_STATUS_LABELS, type WhatsAppTemplate, type WhatsAppTemplateButton } from '@lia/contracts';
import { Call02Icon, Copy01Icon, File02Icon, Film01Icon, Image01Icon, LinkSquare02Icon, Location01Icon, MailReply01Icon, Tick02Icon, UnfoldMoreIcon } from '@hugeicons/core-free-icons';
import { Command } from 'cmdk';
import { Popover as PopoverPrimitive } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { StatusBadge, type StatusTone } from '../primitives/badge.js';
import { Button } from '../primitives/button.js';
import { Checkbox } from '../primitives/checkbox.js';
import { Icon, type IconData } from '../primitives/icon.js';
import { controlBase, Input } from '../primitives/input.js';
import { InlineNotice } from '../primitives/notice.js';

// WhatsApp templates (PRD §13 "Template management"): a WhatsApp-like bubble preview and a searchable picker that sets
// the template name + language together. Templates come from the provider sync only — nothing here invents one.

const PLACEHOLDER = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

export const WHATSAPP_TEMPLATE_STATUS_TONE: Record<string, StatusTone> = {
  APPROVED: 'success',
  PENDING: 'warning',
  IN_APPEAL: 'warning',
  PAUSED: 'warning',
  REJECTED: 'danger',
  DISABLED: 'danger',
  PENDING_DELETION: 'danger',
  LIMIT_EXCEEDED: 'danger',
};

export const whatsAppTemplateStatusLabel = (status: string) => WHATSAPP_TEMPLATE_STATUS_LABELS[status] ?? status;
export const whatsAppTemplateCategoryLabel = (category: string) => WHATSAPP_TEMPLATE_CATEGORY_LABELS[category] ?? category;
export const whatsAppTemplateKey = (t: { name: string; language: string }) => `${t.name}|${t.language}`;

/** Provider status as a badge (icon + Spanish text; unknown statuses shown as reported). */
export function WhatsAppTemplateStatusBadge({ status, className }: { status: string; className?: string }) {
  return (
    <StatusBadge tone={WHATSAPP_TEMPLATE_STATUS_TONE[status] ?? 'neutral'} className={className}>
      {whatsAppTemplateStatusLabel(status)}
    </StatusBadge>
  );
}

/** The template matching a saved name + language (language compared case-insensitively), or null. */
export function findWhatsAppTemplate<T extends { name: string; language: string }>(templates: readonly T[], value: { name: string; language: string } | null): T | null {
  if (!value) return null;
  return templates.find((t) => t.name === value.name && t.language.toLowerCase() === value.language.toLowerCase()) ?? null;
}

// ───────── Preview ─────────

const MEDIA_HEADER: Record<string, { icon: IconData; label: string }> = {
  image: { icon: Image01Icon, label: 'Imagen' },
  video: { icon: Film01Icon, label: 'Video' },
  document: { icon: File02Icon, label: 'Documento' },
  location: { icon: Location01Icon, label: 'Ubicación' },
};

const BUTTON_ICON: Record<WhatsAppTemplateButton['type'], IconData> = {
  quick_reply: MailReply01Icon,
  url: LinkSquare02Icon,
  phone_number: Call02Icon,
  copy_code: Copy01Icon,
  other: Tick02Icon,
};

export interface WhatsAppTemplatePreviewProps {
  template: WhatsAppTemplate;
  /** Values for the variables (key → text). They win over the approved examples. */
  values?: Readonly<Record<string, string>>;
  /** examples: fill missing values with the approved examples · placeholders: show {{key}}. */
  fill?: 'examples' | 'placeholders';
  className?: string;
}

function Filled({ text, values, examples, fill }: { text: string; values: Readonly<Record<string, string>>; examples: Record<string, string | null>; fill: 'examples' | 'placeholders' }) {
  const out: React.ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(PLACEHOLDER)) {
    const key = m[1]!;
    if (m.index! > last) out.push(text.slice(last, m.index));
    const given = values[key]?.trim();
    const shown = given || (fill === 'examples' ? examples[key] : null) || `{{${key}}}`;
    out.push(
      <mark key={`${key}-${m.index}`} title={`Variable {{${key}}}`} data-variable={key} className={cn('rounded-[4px] px-1 py-px', given || shown !== `{{${key}}}` ? 'bg-info-bg text-info' : 'bg-warning-bg text-warning')}>
        {shown}
      </mark>,
    );
    last = m.index! + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return <>{out}</>;
}

/** A template rendered as a WhatsApp message: header (text or media placeholder), body with highlighted variables, footer and buttons. */
export function WhatsAppTemplatePreview({ template: t, values = {}, fill = 'examples', className }: WhatsAppTemplatePreviewProps) {
  const examples = Object.fromEntries(t.variables.map((v) => [v.key, v.example]));
  const media = MEDIA_HEADER[t.headerType];
  const buttons = t.buttons ?? [];
  return (
    <figure aria-label={`Vista previa de la plantilla ${t.name} (${t.language})`} className={cn('m-0 grid justify-items-start gap-1 rounded-card bg-surface-subtle p-3', className)}>
      <div className="grid w-full max-w-[360px] gap-1.5 rounded-card rounded-tl-[4px] border border-border-subtle bg-surface-default px-3 py-2.5 text-body text-text-primary shadow-card">
        {media ? (
          <div className="flex h-24 items-center justify-center gap-2 rounded-control bg-surface-subtle text-meta text-text-secondary">
            <Icon icon={media.icon} aria-hidden className="size-5" />
            <span>{media.label} (se adjunta al enviar)</span>
          </div>
        ) : null}
        {t.headerType === 'text' && t.headerText ? (
          <p className="m-0 font-semibold whitespace-pre-wrap break-words">
            <Filled text={t.headerText} values={values} examples={examples} fill={fill} />
          </p>
        ) : null}
        <p className="m-0 whitespace-pre-wrap break-words">{t.bodyText ? <Filled text={t.bodyText} values={values} examples={examples} fill={fill} /> : <span className="text-text-secondary">(Sin texto de cuerpo)</span>}</p>
        {t.footerText ? <p className="m-0 text-meta text-text-secondary">{t.footerText}</p> : null}
      </div>
      {buttons.length ? (
        <ul aria-label="Botones" className="m-0 grid w-full max-w-[360px] list-none gap-1 p-0">
          {buttons.map((b, i) => (
            <li key={`${b.type}-${i}`} className="grid justify-items-center gap-0.5 rounded-control border border-border-subtle bg-surface-default px-3 py-1.5 text-center">
              <span className="inline-flex items-center gap-1.5 text-body font-medium text-brand-primary">
                <Icon icon={BUTTON_ICON[b.type]} aria-hidden className="size-4" />
                {b.text}
              </span>
              {b.url ? <span className="break-all text-meta text-text-secondary">{b.url}</span> : null}
              {b.phoneNumber ? <span className="text-meta text-text-secondary">{b.phoneNumber}</span> : null}
              {b.type === 'copy_code' && b.example ? <span className="text-meta text-text-secondary">Ejemplo: {b.example}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </figure>
  );
}

// ───────── Picker ─────────

export interface WhatsAppTemplatePickerValue {
  name: string;
  language: string;
}

/** ready: `templates` came from a sync · loading · no_connection · never_synced · error: free text only. */
export type WhatsAppTemplatePickerState = 'ready' | 'loading' | 'no_connection' | 'never_synced' | 'error';

export interface WhatsAppTemplatePickerProps {
  value: WhatsAppTemplatePickerValue | null;
  /** Name + language change together; `template` is the synced template (null for free text / cleared). */
  onValueChange: (value: WhatsAppTemplatePickerValue | null, template: WhatsAppTemplate | null) => void;
  /** Synced templates (one per name + language). */
  templates: readonly WhatsAppTemplate[];
  state?: WhatsAppTemplatePickerState;
  'aria-label': string;
  /** Injected by FormField. */
  id?: string;
  'aria-describedby'?: string;
  disabled?: boolean;
  /** Shows an option that clears the value (optional settings). */
  clearLabel?: string;
  /** Link/button to sync templates, shown with the free-text fallback. */
  syncAction?: React.ReactNode;
  /** Language used when typing a name by hand (default es_MX). */
  defaultLanguage?: string;
  namePlaceholder?: string;
  className?: string;
}

const CLEAR = '__ef_clear__';

const FALLBACK_TEXT: Record<WhatsAppTemplatePickerState, string> = {
  ready: 'No hay plantillas sincronizadas: escribe el nombre exacto de la plantilla aprobada.',
  loading: 'Cargando plantillas…',
  no_connection: 'No hay una conexión de WhatsApp: escribe el nombre exacto de la plantilla aprobada.',
  never_synced: 'Las plantillas aún no se han sincronizado: escribe el nombre exacto o sincronízalas.',
  error: 'No se pudieron cargar las plantillas: escribe el nombre exacto de la plantilla aprobada.',
};

/**
 * Searchable template picker (name, language, status, category, body snippet; preview of the highlighted template).
 * Shows only sendable templates unless «Mostrar también…» is on. A saved value missing from WhatsApp is kept and
 * flagged; without synced templates it is a free-text name + language pair.
 */
export function WhatsAppTemplatePicker({ value, onValueChange, templates, state = 'ready', disabled, clearLabel, syncAction, defaultLanguage = 'es_MX', namePlaceholder, className, ...rest }: WhatsAppTemplatePickerProps) {
  const label = rest['aria-label'];
  const [manual, setManual] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [showAll, setShowAll] = React.useState(false);
  const [active, setActive] = React.useState('');
  const hasList = state === 'ready' && templates.length > 0;
  const listMode = hasList && !manual;
  const selected = findWhatsAppTemplate(templates, value);
  const unknown = hasList && !!value && !selected;
  const visible = templates.filter((t) => showAll || t.sendable || (selected && whatsAppTemplateKey(t) === whatsAppTemplateKey(selected)));
  const highlighted = visible.find((t) => whatsAppTemplateKey(t) === active) ?? selected ?? visible[0] ?? null;

  const choose = (key: string) => {
    setOpen(false);
    if (key === CLEAR) return onValueChange(null, null);
    const t = templates.find((x) => whatsAppTemplateKey(x) === key);
    if (t) onValueChange({ name: t.name, language: t.language }, t);
  };
  const typed = (name: string, language: string) => {
    if (!name.trim()) return onValueChange(null, null);
    const next = { name, language };
    onValueChange(next, findWhatsAppTemplate(templates, next));
  };

  return (
    <div className={cn('grid gap-1.5', className)}>
      {listMode ? (
        <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
          <PopoverPrimitive.Trigger
            id={rest.id}
            aria-describedby={rest['aria-describedby']}
            aria-label={label}
            disabled={disabled}
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            className={cn(controlBase, 'flex items-center justify-between gap-2 text-left', !value && 'text-text-secondary')}
          >
            <span className="truncate">{value ? `${value.name} · ${value.language}` : 'Elegir plantilla…'}</span>
            <Icon icon={UnfoldMoreIcon} aria-hidden className="size-[18px] shrink-0 text-text-secondary" />
          </PopoverPrimitive.Trigger>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="start"
              sideOffset={6}
              className="z-50 grid w-[min(760px,calc(100vw-32px))] gap-2 rounded-menu border border-border-subtle bg-surface-default p-2 shadow-overlay animate-fade-in md:grid-cols-[minmax(0,1fr)_300px]"
            >
              <Command loop label={label} value={active} onValueChange={setActive} className="grid content-start gap-1">
                <Command.Input autoFocus placeholder="Buscar por nombre, idioma o texto…" aria-label="Buscar plantillas" className={cn(controlBase, 'h-9')} />
                <div className="px-1 py-1">
                  <Checkbox checked={showAll} onCheckedChange={(c) => setShowAll(c === true)} label="Mostrar también las que no se pueden enviar" />
                </div>
                <Command.List className="max-h-80 overflow-auto">
                  <Command.Empty className="px-3 py-2 text-body text-text-secondary">Sin coincidencias.</Command.Empty>
                  {clearLabel ? (
                    <Command.Item value={CLEAR} onSelect={() => choose(CLEAR)} className="flex cursor-pointer items-center gap-2 rounded-control px-3 py-2 text-body text-text-secondary outline-none data-[selected=true]:bg-surface-subtle">
                      {clearLabel}
                    </Command.Item>
                  ) : null}
                  {visible.map((t) => {
                    const key = whatsAppTemplateKey(t);
                    const isSel = !!selected && whatsAppTemplateKey(selected) === key;
                    return (
                      <Command.Item
                        key={key}
                        value={key}
                        keywords={[t.name, t.language, whatsAppTemplateCategoryLabel(t.category), t.bodyText, t.headerText ?? '']}
                        onSelect={() => choose(key)}
                        className="grid cursor-pointer gap-0.5 rounded-control px-3 py-2 text-body text-text-primary outline-none data-[selected=true]:bg-surface-subtle"
                      >
                        <span className="flex flex-wrap items-center gap-2">
                          <Icon icon={Tick02Icon} aria-hidden className={cn('size-4 shrink-0 text-brand-primary', isSel ? 'opacity-100' : 'opacity-0')} />
                          <span className="font-medium">{t.name}</span>
                          <span className="text-meta text-text-secondary">{t.language}</span>
                          <span className="text-meta text-text-secondary">{whatsAppTemplateCategoryLabel(t.category)}</span>
                          <WhatsAppTemplateStatusBadge status={t.status} />
                          {isSel ? <span className="sr-only">(seleccionada)</span> : null}
                        </span>
                        <span className="truncate pl-6 text-meta text-text-secondary">{t.bodyText}</span>
                        {!t.sendable && t.unsendableReason ? <span className="pl-6 text-meta text-warning">{t.unsendableReason}</span> : null}
                      </Command.Item>
                    );
                  })}
                </Command.List>
              </Command>
              {highlighted ? <WhatsAppTemplatePreview template={highlighted} className="hidden content-start md:grid" /> : null}
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      ) : (
        <>
          {!hasList ? (
            <InlineNotice tone={state === 'error' ? 'warning' : 'info'} role="note" as="div">
              <span>{FALLBACK_TEXT[state]}</span>
              {syncAction && state !== 'loading' ? <span className="ml-1">{syncAction}</span> : null}
            </InlineNotice>
          ) : null}
          <div className="grid gap-2 sm:grid-cols-[2fr_1fr]">
            <Input id={rest.id} aria-describedby={rest['aria-describedby']} aria-label={`${label}: nombre`} value={value?.name ?? ''} placeholder={namePlaceholder} disabled={disabled} onChange={(e) => typed(e.target.value, value?.language ?? defaultLanguage)} />
            <Input aria-label={`${label}: idioma`} value={value?.language ?? defaultLanguage} disabled={disabled || !value} onChange={(e) => value && typed(value.name, e.target.value)} />
          </div>
        </>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {unknown ? <span className="text-meta text-warning">No encontrada en WhatsApp</span> : null}
        {selected && !selected.sendable ? <span className="text-meta text-warning">No se puede enviar: {selected.unsendableReason ?? 'no está aprobada'}</span> : null}
        {hasList && !disabled ? (
          <Button type="button" size="sm" variant="ghost" aria-label={`${listMode ? 'Escribir el nombre' : 'Elegir de la lista'}: ${label}`} onClick={() => setManual(listMode)}>
            {listMode ? 'Escribir el nombre' : 'Elegir de la lista'}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
